import { useRef } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import { DashboardModalWithCloseButton, ModalContent } from '@/shared/components/dashboards/Modal';
import { OperatorLogo } from '@/shared/components/operators/Logos';
import { formatBps, formatPct } from '@/shared/utils/strings';
import type TokenAmount from '@/shared/utils/TokenAmount';
import { type OperatorSummary } from '../../../hooks/useOperatorsAndAuction';

export default function OperatorSelectionModal({
  open,
  onClose,
  setSelectedOperator,
  selectedOperator,
  delegationAmount,
  operators,
  allowClosedDelegation = false,
}: {
  open: boolean;
  onClose: () => void;
  setSelectedOperator: (operator: OperatorSummary) => void;
  selectedOperator: OperatorSummary | undefined;
  delegationAmount: TokenAmount | undefined;
  operators: OperatorSummary[];
  allowClosedDelegation?: boolean;
}) {
  const minFee =
    operators.length > 0
      ? ` starting at ${formatBps(Math.min(...operators.map((o) => o.nextEpoch.commissionBps)))}`
      : '';

  const operatorRef = useRef<OperatorSummary[] | undefined>(undefined);

  if (open && !operatorRef.current) {
    operatorRef.current = operators
      .filter(
        (o) =>
          ((allowClosedDelegation && !o.openDelegationEnabled) || o.isDelegationAllowed) &&
          o.allValidators.totalCount > 0,
      )
      .sort(() => Math.random() - 0.5);
  }

  return (
    <DashboardModalWithCloseButton
      open={open}
      onClose={onClose}
      afterLeave={() => {
        operatorRef.current = undefined;
      }}
    >
      <ModalContent
        title="Choose an Operator"
        description={`Operators who are open to delegation earn rewards on your behalf. In return, they take a delegation commission${minFee}.`}
        scrollableContent
      >
        <div className="flex flex-col gap-y-2 font-aeonikMedium text-14">
          {operatorRef.current?.map((o) => (
            <button
              key={o.idSs58}
              type="button"
              className="flex w-full gap-x-4 whitespace-nowrap rounded-md p-3 transition hover:border-cf-gray-4 hover:bg-cf-gray-3-5"
              onClick={() => {
                setSelectedOperator(o);
                onClose();
              }}
            >
              <div className="flex w-full items-center gap-x-2">
                <OperatorLogo size="large" idSs58={o.idSs58} />
                <div className="flex w-full flex-col items-start justify-between">
                  <div className="flex w-full items-center justify-between text-cf-white">
                    <div className="flex items-center gap-x-1">
                      <span>{o.alias || 'Operator'}</span>
                      {selectedOperator?.idSs58 === o.idSs58 && (
                        <>
                          <span className="text-cf-light-2">·</span>
                          <span className="text-cf-green-1">Selected</span>
                        </>
                      )}
                      {/* eslint-enable no-nested-ternary */}
                    </div>
                    <span>
                      {formatPct(o.nextEpoch.calculateDelegatorApy(delegationAmount))} APY
                    </span>
                  </div>
                  <div className="flex w-full items-center justify-between text-cf-light-2">
                    <span>{abbreviate(o.idSs58)}</span>
                    <span>{formatBps(o.nextEpoch.commissionBps)} commission</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </ModalContent>
    </DashboardModalWithCloseButton>
  );
}
